import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { CostCenterPriceModel } from 'src/app/models/archive/cost-center-price.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { TransferConstructionSiteDetailModel } from 'src/app/models/rental/transfer-construction-site-detail.model';
import { TransferConstructionSiteModel } from 'src/app/models/rental/transfer-construction-site.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TransferConstructionSiteDetailService {

  private readonly _httpClient = inject(HttpClient);

  createTransferConstructionSiteDetail(transferConstructionSiteDetailContract: TransferConstructionSiteDetailModel): Observable<TransferConstructionSiteDetailModel> {
    return this._httpClient.post<TransferConstructionSiteDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.CreateTransferConstructionSiteDetail}`,
      transferConstructionSiteDetailContract);
  }

  createCostCenterPriceDestinyByOrigin(originCostCenterId: number, destinityCostCenterId: number, conceptInvoicingId: number, articleId: number): Observable<CostCenterPriceModel> {
    return this._httpClient.post<CostCenterPriceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.CreateCostCenterPriceDestinyByOrigin}`,
      { originCostCenterId, destinityCostCenterId, conceptInvoicingId, articleId });
  }

  createDetails(transferConstructionSiteDetails: Array<TransferConstructionSiteDetailModel>, transferConstructionSiteId: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.CreateDetailsAsync}/${transferConstructionSiteId}`,
      transferConstructionSiteDetails);
  }

  deleteSomeDetails(transferConstructionSiteDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.DeleteSomeDetails}`,
      transferConstructionSiteDetailIds);
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAllTransferConstructionSiteDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, storeConsolidated: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('storeConsolidated', storeConsolidated);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetAllTransferConstructionSiteDetailsWithPagination}`,
      filters, { params: params }
    );
  }

  getArticlesForDetails(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], transferConstructionSiteId: number): Observable<PagedResponseModel<RentalStockModalModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('transferConstructionSiteId', transferConstructionSiteId);
    return this._httpClient.post<PagedResponseModel<RentalStockModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetArticlesForDetailsAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetColumnsForArticles}`);
  }

  getColumnsForDetailConsultation(): Observable<FilterGlobalResponseModel> {
    return this._httpClient.get<FilterGlobalResponseModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetColumnsForDetailConsultation}`);
  }

  getColumnsForDetailsTransferConstructionSite(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetColumnsForDetailsTransferConstructionSiteAsync}`);
  }

  getListStocksInCostCenter(transferConstructionSite: TransferConstructionSiteModel): Observable<Array<RentalStockModel>> {
    return this._httpClient.post<Array<RentalStockModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetListStocksInCostCenter}`,
      transferConstructionSite);
  }

  getStocksByArticleIdStoreId(rentalStockModel: RentalStockModel): Observable<number> {
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetStocksByArticleIdStoreId}`,
      rentalStockModel);
  }

  getStockInCostCenterByArticleStore(transferConstructionSiteDetail: TransferConstructionSiteModel, articleId: number, storeId: number): Observable<number> {
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetListStocksInCostCenter}/${articleId}/${storeId}`,
      transferConstructionSiteDetail);
  }

  getTransferConstructionSiteDetailById(transferConstructionSiteDetailId: number): Observable<TransferConstructionSiteDetailModel> {
    return this._httpClient.get<TransferConstructionSiteDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetTransferConstructionSiteDetailById}/${transferConstructionSiteDetailId}`);
  }

  getTransferConstructionSiteDetailByKeys(transferConstructionSiteDetailId: number, storeId: number, articleId: number): Observable<TransferConstructionSiteDetailModel> {
    const params = new HttpParams().set('transferConstructionSiteDetailId', transferConstructionSiteDetailId).set('storeId', storeId).set('articleId', articleId);
    return this._httpClient.get<TransferConstructionSiteDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetTransferConstructionSiteDetailByKeys}`,
      { params: params });
  }

  getTransferDetailsByTransferIdWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, transferConstructionSiteId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('transferConstructionSiteId', transferConstructionSiteId);
    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.GetTransferDetailsByTransferIdWithPagination}`,
      { params }
    );
  }

  updateTransferConstructionSiteDetail(transferConstructionSiteDetailContract: TransferConstructionSiteDetailModel): Observable<TransferConstructionSiteDetailModel> {
    return this._httpClient.put<TransferConstructionSiteDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.transferConstructionSiteDetail.UpdateTransferConstructionSiteDetail}`,
      transferConstructionSiteDetailContract);
  }
}
